// Somebody signed in or out in ANOTHER tab of this browser — what should this tab do?
//
// ⚠️ FOUND BY ROB ON STAGING, 16/10/2026. He had Harvey Specter open in one tab and signed in
// as Elon Musk in another. Signing in writes the new session to the browser's shared storage,
// and every request this app makes reads it from there (`lib/api.js`, `createApiClient`) — but
// a tab that is already open keeps SHOWING whoever it loaded with. So the screen said
// "Harvey Specter" while every click acted as Elon: Rob tried to rebook Harvey's day, booked
// ELON off instead (an admin's own leave approves at once), and was then told "already off".
// On a shared office PC the same thing would let somebody book or approve leave as the wrong
// person with their own name on the screen.
//
// Rob's answer B1: an open tab reloads itself the moment somebody signs in or out elsewhere,
// so it can never show one person and act as another. A reload loses anything typed and not
// saved in that tab — the lesser harm, by a distance.
//
// ⚠️ NO REACT AND NO IMPORTS: `signInElsewhere.checks.mjs` imports this file under plain node.

// The browser storage key the session lives under — the same one `AuthContext` and `lib/api.js` use.
export const TOKEN_KEY = 'token';
// Kept for ONE tab across its own reload (sessionStorage is per tab), so it can say why it reloaded.
export const NOTICE_KEY = 'hh_signed_in_elsewhere';

export const SWITCHED = 'switched';
export const SIGNED_OUT = 'signed_out';
export const SIGNED_IN = 'signed_in';

// Who a session belongs to: the person and their company, read from the token itself.
// ⚠️ Read only to COMPARE two sessions in this browser — never trusted for anything else. The
// server checks every token on every request; this decides nothing but whether to reload.
export function personOf(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    // `atob` exists in every browser and in node 16+, where the checks run.
    const payload = JSON.parse(atob(padded));
    if (!payload || !payload.sub) return null;
    return `${payload.tenant_id || ''}:${payload.sub}`;
  } catch {
    return null;
  }
}

// What a `storage` event from another tab means for this one: SWITCHED, SIGNED_OUT,
// SIGNED_IN, or null for "nothing to do".
//
//   key        — the event's key (null when another tab cleared all storage)
//   mine       — the session this tab is showing
//   theirs     — the session now in storage
//
// ⚠️ THE SAME PERSON SIGNING IN AGAIN IS NOT A SWITCH. A fresh token for the same person and
// company changes nothing anybody can see, so the tab is left alone rather than reloaded
// under somebody's typing for no reason.
export function whatChanged({ key, mine, theirs }) {
  if (key !== TOKEN_KEY && key !== null) return null;
  const before = mine || null;
  const after = theirs || null;
  if (before === after) return null;
  if (before && !after) return SIGNED_OUT;
  if (!before && after) return SIGNED_IN;
  const a = personOf(before);
  const b = personOf(after);
  if (a && b && a === b) return null;
  return SWITCHED;
}
